// === EXPORT DES BUG REPORTS EN MARKDOWN ===
// Permet à un testeur (Joris) d'exporter localement tous ses bug reports
// dans un fichier .md qu'il peut coller directement dans une session
// Claude pour que je travaille dessus sans avoir besoin d'aller chercher
// chaque rapport sur Discord ou de me les réécrire à la main.
//
// Le format MD est :
// - Lisible humain (pour review avant envoi)
// - Parseable assistant (titres, tableaux, sections nommées avec emojis)
// - Auto-suffisant (date d'export, version du jeu, nombre de rapports)

import { BugReport, BugReportManager } from './BugReportManager';
import { GAME_VERSION } from './MetaManager';
import { downloadTextFile } from '../utils/FileIO';

const EXPORT_FORMAT_VERSION = 1;

/** Slugifie un pseudo pour un nom de fichier sûr (ascii lowercase + tirets). */
function slugify(input: string): string {
  return input
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')
    .slice(0, 40) || 'anonyme';
}

/** Échappe les pipes pour rester valide dans une cellule de tableau MD. */
function escapeCell(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\n/g, ' ');
}

/**
 * Formate un seul rapport en bloc Markdown auto-contenu.
 * Le titre inclut le suffixe de l'ID pour matcher la convention Discord
 * (ex: "Bug #k2qsq — Jowaitis").
 */
export function formatBugReportAsMarkdown(report: BugReport): string {
  // Récupère le suffixe court de l'ID (les 5 derniers caractères) pour matcher
  // l'affichage Discord/UI (ex: bug_1747232345_k2qsq → k2qsq).
  const idSuffix = report.id.split('_').pop() ?? report.id;
  const sentStatus = report.sent
    ? '✅ Oui'
    : report.sendError
      ? `❌ Échec — ${escapeCell(report.sendError)}`
      : '⏳ Local uniquement';

  const lines = [
    `## Bug #${idSuffix} — ${report.pseudo}`,
    '',
    '| Champ | Valeur |',
    '|---|---|',
    `| **ID complet** | \`${report.id}\` |`,
    `| **Date** | ${report.timestamp} |`,
    `| **Pseudo** | ${escapeCell(report.pseudo)} |`,
    `| **Version** | v${report.version} |`,
    `| **Scène** | ${escapeCell(report.scene)} |`,
    `| **Résolution** | ${escapeCell(report.resolution)} |`,
    `| **User Agent** | ${escapeCell(report.userAgent)} |`,
    `| **Envoyé Discord** | ${sentStatus} |`,
    '',
    '### 📝 Description',
    '',
    report.description.trim() || '_(vide)_',
    '',
  ];

  if (report.context && report.context.trim().length > 0) {
    lines.push('### 🗺️ Contexte run', '', report.context.trim(), '');
  }

  return lines.join('\n');
}

/**
 * Formate la liste complète des rapports en un seul document Markdown,
 * avec un en-tête de méta (date d'export, version courante du jeu, nombre
 * de rapports) pour que je puisse situer le contexte sans devoir demander.
 */
export function formatAllBugReportsAsMarkdown(reports: BugReport[]): string {
  const exportedAt = new Date().toISOString();
  const header = [
    '# Bug Reports — Deadtide',
    '',
    `- **Exporté le** : ${exportedAt}`,
    `- **Version du jeu (au moment de l'export)** : v${GAME_VERSION}`,
    `- **Nombre de rapports** : ${reports.length}`,
    `- **Format export** : v${EXPORT_FORMAT_VERSION}`,
    '',
    '---',
    '',
  ].join('\n');

  if (reports.length === 0) {
    return header + '_Aucun bug report dans l\'historique local._\n';
  }

  // Les rapports sont déjà triés du plus récent au plus ancien par BugReportManager
  // (unshift dans submit). On garde cet ordre dans l'export.
  const blocks = reports.map(formatBugReportAsMarkdown).join('\n---\n\n');
  return header + blocks;
}

/** Nom de fichier suggéré pour l'export (pseudo + date ISO courte). */
export function suggestExportFilename(reports: BugReport[]): string {
  const pseudo = reports[0]?.pseudo ?? 'anonyme';
  const date = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
  return `deadtide-bug-reports-${slugify(pseudo)}-${date}.md`;
}

/**
 * Déclenche le téléchargement du fichier MD avec tous les rapports locaux.
 * Retourne le nombre de rapports exportés (0 si aucun ; le fichier est
 * quand même téléchargé pour confirmer à l'utilisateur que l'action a
 * fonctionné).
 */
export function downloadAllBugReportsAsMarkdown(): number {
  const reports = BugReportManager.getAll();
  const md = formatAllBugReportsAsMarkdown(reports);
  const filename = suggestExportFilename(reports);
  downloadTextFile(filename, md, 'text/markdown');
  return reports.length;
}
